import { isObjectLiteral } from '../data';
import { pickString } from '../string';
const FALLBACK_MESSAGE = 'Something went wrong';
// Any object, not just a literal: a rejected request arrives as an Error with the envelope copied onto
// it, and that still deserves the envelope's message and error_id rather than the generic Error branch.
export function hasEnvelope(raw) {
    return !!raw && typeof raw === 'object' && isObjectLiteral(raw.envelope);
}
function pickFromDetails(details, key) {
    if (!Array.isArray(details))
        return undefined;
    for (const d of details) {
        if (isObjectLiteral(d) && d[key] != null)
            return d[key];
    }
    return undefined;
}
function extractSourceService(source) {
    var _a;
    if (!source)
        return undefined;
    return (_a = source.custom_service_name) !== null && _a !== void 0 ? _a : (source.service != null ? String(source.service) : undefined);
}
function stringifyTrace(value) {
    if (value == null)
        return undefined;
    if (typeof value === 'string')
        return value;
    if (Array.isArray(value))
        return value.join('\n');
    return undefined;
}
function deriveEnvelopeDisplay(envelope) {
    const displayMessage = envelope.user_message || envelope.message || FALLBACK_MESSAGE;
    const displayDetail = envelope.message && envelope.message !== displayMessage ? envelope.message : undefined;
    return { displayMessage, displayDetail };
}
function normalizeFromEnvelope(raw) {
    const { envelope } = raw;
    const { displayMessage, displayDetail } = deriveEnvelopeDisplay(envelope);
    const traceContext = pickFromDetails(envelope.details, 'trace_context');
    const source = pickFromDetails(envelope.details, 'source');
    return {
        displayMessage,
        displayDetail,
        errorId: envelope.error_id,
        code: envelope.code,
        errorCode: envelope.error_code,
        traceId: traceContext === null || traceContext === void 0 ? void 0 : traceContext.trace_id,
        spanId: traceContext === null || traceContext === void 0 ? void 0 : traceContext.span_id,
        sourceService: extractSourceService(source),
        raw
    };
}
function normalizeFromLegacy(raw) {
    if (typeof raw === 'string') {
        return { displayMessage: raw || FALLBACK_MESSAGE, raw };
    }
    if (raw instanceof Error) {
        // `stack` is the last resort: several call sites put a backend trace there deliberately, so it is
        // display material, and anything wanting a JS stack for reporting must not route it through here.
        const carried = raw;
        return {
            displayMessage: raw.message || FALLBACK_MESSAGE,
            displayDetail: stringifyTrace(carried.trace) || pickString(carried.data) || raw.stack,
            raw
        };
    }
    if (!isObjectLiteral(raw)) {
        return { displayMessage: FALLBACK_MESSAGE, raw };
    }
    const displayMessage = pickString(raw.message, raw.msg) || FALLBACK_MESSAGE;
    const displayDetail = stringifyTrace(raw.trace) || pickString(raw.stack, raw.data);
    return {
        displayMessage,
        displayDetail,
        code: typeof raw.code === 'number' ? raw.code : undefined,
        errorCode: pickString(raw.type),
        raw
    };
}
export function normalizeError(raw) {
    return hasEnvelope(raw) ? normalizeFromEnvelope(raw) : normalizeFromLegacy(raw);
}
export function hasErrorContent(raw) {
    if (raw == null)
        return false;
    if (typeof raw === 'string')
        return raw.length > 0;
    if (raw instanceof Error)
        return Boolean(raw.message);
    if (!isObjectLiteral(raw))
        return false;
    if (hasEnvelope(raw)) {
        return Boolean(pickString(raw.envelope.user_message, raw.envelope.message));
    }
    return Boolean(pickString(raw.message, raw.msg));
}
